import type { MutableRefObject } from 'react';
import type { MapObject } from '../../../models/mapObject';
import type { CursorUserInfo } from '../../../client';
import type { HighlightManager } from '../controller/HighlightManager';
import type { UseLighthouseResult } from '../useLighthouse';
import type { UseTutorialRewardClaimResult } from '../useMapOnboarding';
import RealEstateCatalogModal from './modals/RealEstateCatalogModal';
import RealEstateInfoHost from './modals/RealEstateInfoHost';
import SalePreviewHost from './modals/SalePreviewHost';
import MapStaticObjectHost from './modals/MapStaticObjectHost';
import WinstonCatHost from './modals/WinstonCatHost';
import NewIslandModal from './modals/NewIslandModal';
import LighthouseHost from './modals/LighthouseHost';
import NavigationConfirmHost from './modals/NavigationConfirmHost';
import PlotFreeHost from './modals/PlotFreeHost';
import PlotTakenHost from './modals/PlotTakenHost';
import MapObjectTypeSelectionHost from './modals/MapObjectTypeSelectionHost';
import DailyRewardsModal from '../../../ui/modals/DailyRewardsModal';
import DailyQuestsHost from './modals/DailyQuestsHost';
import MapWelcomeHost from './modals/MapWelcomeHost';
import MayorStoriesHost from './modals/MayorStoriesHost';
import MapOnboardingHost from './modals/MapOnboardingHost';
import BuildHousePopupHost from './modals/BuildHousePopupHost';
import TutorialRewardHost from './modals/TutorialRewardHost';
import PostBuyMessageHost from './modals/PostBuyMessageHost';
import ComebackOfferHost from './modals/ComebackOfferHost';
import FirstVisitZoomHost from './modals/FirstVisitZoomHost';
import AdminOverlaysHost, { type AdminOverlaysHostProps } from './modals/AdminOverlaysHost';
import FireflyInfoHost from './modals/FireflyInfoHost';
import LiveCursorInfoModal from '../../../ui/modals/LiveCursorInfoModal';
import { PopupDirectorGate, useDirectedPopup } from '../../../ui/popupDirector/popupDirector';
import useMapModalStore from '../../../stores/useMapModalStore';
import useMapOnboardingStore from '../../../stores/useMapOnboardingStore';
import useSplashStore from '../../../stores/useSplashStore';
import { useOnboardingState } from '../../../hooks/useOnboardingState';

/**
 * Thin composition root for map modals + admin/onboarding overlays.
 *
 * Most hosts read open-flags / payloads from useMapModalStore (and related
 * stores) themselves. Props here are only MapCanvas-owned clusters that cannot
 * move into a store without changing behavior (local useState / refs / hook
 * results shared with PIXI handlers).
 *
 * Limit: ≤10 top-level prop members (grouped bags).
 */
interface MapModalsProps {
  highlightManagerRef: MutableRefObject<HighlightManager | null>;
  plots: MapObject[];
  lighthouse: UseLighthouseResult;
  lhWalletConnected: boolean;
  dailyRewards: { isOpen: boolean; onClose: () => void };
  tutorialReward: UseTutorialRewardClaimResult;
  election: {
    showMayorStories: boolean;
    handleElectionVote: () => void;
    handleElectionRegister: () => void;
    dismissMayorStories: () => void;
  };
  admin: AdminOverlaysHostProps;
  firefly: {
    fireflyPopup: CursorUserInfo | null;
    fireflyPopupLoading: boolean;
    fireflyRecordedAt: number | null;
    setFireflyPopup: (value: CursorUserInfo | null) => void;
    setFireflyPopupLoading: (value: boolean) => void;
  };
  cursor: {
    cursorPopup: CursorUserInfo | null;
    cursorPopupLoading: boolean;
    setCursorPopup: (value: CursorUserInfo | null) => void;
    setCursorPopupLoading: (value: boolean) => void;
  };
}

export default function MapModals({
  highlightManagerRef,
  plots,
  lighthouse,
  lhWalletConnected,
  dailyRewards,
  tutorialReward,
  election,
  admin,
  firefly,
  cursor,
}: MapModalsProps) {
  // Popups the player did not ask for wait while any other map window is open
  // and show one at a time (ui/popupDirector).
  const mapWindowOpen = useMapModalStore((s) => s.activeModal !== null);
  const onboardingPopupOpen = useMapOnboardingStore((s) => s.isOnboardingBuildHousePopupOpen || s.firstVisitPhase !== null);
  const { hasOnboardingCompleted, isLoading: onboardingLoading } = useOnboardingState();
  const splashVisible = useSplashStore((s) => s.visible);
  const busy = splashVisible || mapWindowOpen || onboardingPopupOpen || onboardingLoading || !hasOnboardingCompleted()
    || firefly.fireflyPopup !== null || cursor.cursorPopup !== null;
  const showDailyRewards = useDirectedPopup('dailyRewards', dailyRewards.isOpen);
  return (
    <>
        <PopupDirectorGate busy={busy} />
        <RealEstateCatalogModal highlightManagerRef={highlightManagerRef} />
        <RealEstateInfoHost />
        {/* Intermediate sale preview modal — closable, shows house image + price + View house CTA */}
        <SalePreviewHost />
        <MapStaticObjectHost />
        <WinstonCatHost />
        <LighthouseHost
          lighthouse={lighthouse}
          walletConnected={lhWalletConnected}
        />
        <NavigationConfirmHost />
        <PlotFreeHost />
        <PlotTakenHost />
        <MapObjectTypeSelectionHost />
        {/* ТИМЧАСОВИЙ КОД: закоментовано повідомлення про нові будинки */}
        {/* eslint-disable-next-line no-constant-binary-expression -- intentional dead branch: feature temporarily disabled */}
        {false && <NewIslandModal />}
        <DailyRewardsModal
          isOpen={showDailyRewards}
          onClose={dailyRewards.onClose}
        />
        <DailyQuestsHost />
        {/* Map welcome modal — first-ever visit, then redirect to TapScreen */}
        <MapWelcomeHost />
        <MayorStoriesHost
          showMayorStories={election.showMayorStories}
          handleElectionVote={election.handleElectionVote}
          handleElectionRegister={election.handleElectionRegister}
          dismissMayorStories={election.dismissMayorStories}
        />
        <MapOnboardingHost />
        {/* Onboarding popup: "Build your first house" — matches onboarding design */}
        <BuildHousePopupHost plots={plots} />
        {/* Tutorial completion reward modal — single-screen design */}
        <TutorialRewardHost reward={tutorialReward} />
        <PostBuyMessageHost />
        <ComebackOfferHost />
        <FirstVisitZoomHost />
        <AdminOverlaysHost {...admin} />

        {/* Cursor sprites are PIXI Text objects on the viewport — no CSS overlay needed */}

        {/* Firefly replay info popup */}
        <FireflyInfoHost {...firefly} />

        {/* Cursor user info popup */}
        <LiveCursorInfoModal
          info={cursor.cursorPopup}
          isLoading={cursor.cursorPopupLoading}
          onClose={() => {
            cursor.setCursorPopup(null);
            cursor.setCursorPopupLoading(false);
          }}
        />
    </>
  );
}
